import { inject, Injectable } from '@angular/core';
import { GenericDtoAssemblerService } from '@ebeihilfe/core';

import { AntragUpdateDto } from '../dto/antrag-update';
import { PartiallyTypedNewAntrag } from '../model';
import { MetadataHelpersService } from '../services';

@Injectable({
  providedIn: 'root',
})
export class AntragUpdateDtoAssemblerService extends GenericDtoAssemblerService<
  PartiallyTypedNewAntrag,
  AntragUpdateDto
> {
  private readonly metadataHelpersService = inject(MetadataHelpersService);

  public override async toDto(entity: PartiallyTypedNewAntrag): Promise<AntragUpdateDto> {
    // Drafts persist only the raw survey model (surveyDaten); the metadata DTO is assembled at
    // final submit. Metadata is therefore only serialized when a caller (e.g. the Kurzantrag flow)
    // explicitly provides it.
    const encryptedMetadata =
      entity.metadata !== undefined ? await this.metadataHelpersService.serialize(entity.metadata) : undefined;
    const encryptedSurveyDaten =
      entity.surveyData !== undefined
        ? await this.metadataHelpersService.serializeSurveyData(entity.surveyData)
        : undefined;

    return new AntragUpdateDto(entity.antragId, entity.concurrency, encryptedMetadata, encryptedSurveyDaten);
  }

  // Note the intentional asymmetry with toDto: surveyDaten is NOT deserialized here. On the read path
  // it round-trips through EinreichungService.decryptSurveyData → Antrag.setSurveyData, so fromDto only
  // reverses the metadata half of the draft DTO.
  public override async fromDto(dto: AntragUpdateDto): Promise<PartiallyTypedNewAntrag> {
    const decryptedMetadata =
      dto.encryptedMetadata !== undefined
        ? await this.metadataHelpersService.deserialize(dto.encryptedMetadata)
        : undefined;

    return new PartiallyTypedNewAntrag(dto.antragId, dto.concurrency, decryptedMetadata);
  }
}
